import React, { useState, useEffect, useCallback } from "react";
import jwtDecode from "jwt-decode";
import { useDispatch, useSelector } from "react-redux";
import makeStyles from "@mui/styles/makeStyles";
import {
    Avatar,
    Box,
    Chip,
    ListItemIcon,
    Menu,
    MenuItem,
    Link
} from "@mui/material/";

import { HelpOutline, ExitToAppOutlined, Settings } from "@mui/icons-material";
import { strings } from "../i18n";
import { history } from "../lib";
import config from "../../config";
import actions from "../../Store/actions";
import styles from "./styles";
import CheckAvatar from "./CheckAvatar";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate } from "react-router-dom";
import { useHasAdminGroup } from "../hooks";

const routePrefix = config.paths.auth;

/**
 * User Menu Component
 * @param {Props} props
 */
const UserMenu = () => {
    const { supportEmail } = config;
    let navigate = useNavigate();
    const classes = makeStyles(styles)();
    const dispatch = useDispatch();
    const { auth, userOrganisations } = useSelector((state) => state);
    const [name, setName] = useState("");

    /** Menu*/
    const [anchorEl, setAnchorEl] = useState(null);
    const onMenuClose = () => setAnchorEl(null);
    const onMenuOpen = (event: Event) => setAnchorEl(event.currentTarget);
    const org = userOrganisations.item;

    const isAdmin = useHasAdminGroup();
    /** Check for DF Capital org */

    const user = auth.account;

    useEffect(() => {
        if (auth.idToken) {
            const jwt = jwtDecode(auth.idToken);
            setName(jwt.name);
        }
    }, [auth]);

    const onLogoutClick = useCallback(() => {
        navigate(`/${config.paths.logout}`);
    }, []);

    // Router navigation, not an href: a full page load re-runs the MSAL
    // token exchange and can drop the user back on the login page.
    const onSettingsClick = useCallback(() => {
        setAnchorEl(null);
        navigate(`/${config.paths.auth}/${config.paths.settings}`);
    }, [navigate]);

    const onUserOrganisationClick = useCallback(() => {
        if (userOrganisations.data.length > 1) {
            dispatch(actions.userOrganisations.create.itemClear());
        }
    }, [dispatch, userOrganisations]);

    return (
        <>
            <Box className={classes.rightNavigation}>
                <Chip
                    variant="outlined"
                    className={classes.chipContainer}
                    label={user.displayName}
                    avatar={
                        <Box className={classes.avatarContainer}>
                            <CheckAvatar
                                url={user.avatarPath}
                                name={user.displayName}
                            />
                        </Box>
                    }
                    deleteIcon={<ExpandMoreIcon color="primary" />}
                    onDelete={onMenuOpen}
                    onClick={onMenuOpen}
                />
            </Box>
            <Menu
                anchorEl={anchorEl}
                onClose={onMenuClose}
                open={Boolean(anchorEl)}
                className={classes.userMenuPaper}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
                {userOrganisations.item !== null && (
                    <MenuItem onClick={onUserOrganisationClick}>
                        <ListItemIcon>
                            <Avatar src={userOrganisations.item.logoPath} />
                        </ListItemIcon>
                        {userOrganisations.item.name}
                    </MenuItem>
                )}
                <MenuItem onClick={onSettingsClick}>
                    <ListItemIcon>
                        <Settings />
                    </ListItemIcon>
                    {strings("settings")}
                </MenuItem>
                <MenuItem>
                    <ListItemIcon>
                        <HelpOutline />
                    </ListItemIcon>
                    <Link
                        href={`mailto:${supportEmail}`}
                        color="inherit"
                        underline="none"
                    >
                        {strings("needHelp")}
                    </Link>
                </MenuItem>
                <MenuItem onClick={onLogoutClick}>
                    <ListItemIcon>
                        <ExitToAppOutlined fontSize="medium" />
                    </ListItemIcon>
                    {strings("logout")}
                </MenuItem>
            </Menu>
        </>
    );
};

export default UserMenu;
